import api from './api';
import type {
  ApiResponse,
  PaginatedResponse,
  Reminder,
  ReminderFilters,
  CreateReminderData,
  UpdateReminderData,
} from '@/types';

// Get reminders with filtering and pagination
export const getReminders = async (filters: ReminderFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get<PaginatedResponse<Reminder>>(`/reminders?${params}`);
  return response.data;
};

// Get single reminder
export const getReminder = async (id: string) => {
  const response = await api.get<ApiResponse<Reminder>>(`/reminders/${id}`);
  return response.data.data;
};

// Create reminder
export const createReminder = async (data: CreateReminderData) => {
  const response = await api.post<ApiResponse<Reminder>>('/reminders', data);
  return response.data.data;
};

// Update reminder
export const updateReminder = async (id: string, data: UpdateReminderData) => {
  const response = await api.put<ApiResponse<Reminder>>(`/reminders/${id}`, data);
  return response.data.data;
};

// Delete reminder
export const deleteReminder = async (id: string) => {
  await api.delete(`/reminders/${id}`);
};

// Mark single reminder as read
export const markReminderRead = async (id: string) => {
  const response = await api.patch<ApiResponse<Reminder>>(`/reminders/${id}/read`);
  return response.data.data;
};

// Mark all reminders as read
export const markAllRemindersRead = async () => {
  const response = await api.post<{ message: string; count: number }>('/reminders/mark-all-read');
  return response.data;
};

// Get unread reminder count (for badge)
export const getUnreadCount = async () => {
  const response = await api.get<{ count: number }>('/reminders/unread-count');
  return response.data.count;
};

// Get upcoming reminders (for dropdown preview)
export const getUpcomingReminders = async (limit = 5) => {
  const response = await api.get<{ data: Reminder[] }>(`/reminders/upcoming?limit=${limit}`);
  return response.data.data;
};

/**
 * Per-role dashboard — reminders whose `remind_at` has already elapsed and are
 * still unread ("due today / overdue"). Thin wrapper over the index endpoint's
 * `timing=due` + `status=unread` scopes; already scoped to the caller by
 * `Reminder::forUser()` server-side. Returns just the rows (the dashboard rail
 * has no pager); use {@link getReminders} directly when you need `meta`.
 */
export const getDueReminders = async (limit = 5): Promise<Reminder[]> => {
  const page = await getReminders({
    timing: 'due',
    status: 'unread',
    sort: 'remind_at',
    direction: 'asc',
    per_page: limit,
  });
  return page.data;
};
